import { any, ascend, compose, equals, filter, groupWith, isNil, pathOr, prop, sortWith, splitAt, values } from 'ramda'
import spacetime from 'spacetime'
import { Message, MessageAttributeKeys, MessageAttributes, MessageAuthorType, MessageContentType } from 'types/message'
import { isNilOrEmpty } from 'utils'

export const orderMessages = sortWith<Message>([
  ascend(prop('createdAt'))
])

export const shouldReceiveMessage = (message: Message): boolean => {
  const isNonEmptyChat = message.contentType === MessageContentType.CHAT && (
    !!message.body.length || !!message.attributes.appointmentInfo || !!message.attributes.startInteraction
  )
  const isEmailCapture = message.contentType === MessageContentType.EMAIL_CAPTURE_CARD
  const hasAttachments = message.attachments && message.attachments.length > 0
  const hasConversationRating = isRateConversationCTAMessage(message) || isConversationRatingMessage(message)

  return isNonEmptyChat || isEmailCapture || hasAttachments || hasConversationRating
}

export const toVisibleMessagesArray =
  compose<Record<string, Message>, Message[], Message[], Message[], Message[]>(
    filter((message: Message) => !!message.body || !hasMessageAttribute(message, 'startInteraction')),
    filter(shouldReceiveMessage),
    orderMessages,
    values
  )

const shouldGroupMessages = (a: Message, b: Message) =>
  (isMeetingScheduleMessage(a) && isMeetingScheduleMessage(b)) || (
    equals(a.authorType, b.authorType) && equals(a.authorId, b.authorId) &&
    spacetime(b.attributes.generatedAt || b.createdAt)
      .isBefore(
        spacetime(a.attributes.generatedAt || a.createdAt)
          .add(2, 'hour')
      )
  )

export const groupMessages = (orderedMessages: Message[]): Message[][] => {
  return groupWith(shouldGroupMessages, orderedMessages)
}

export const isEmptyMessage = (body: string) => !body || !body.replace(/<\/?p[^>]*>/g, '').trim()

export const isMeetingScheduleMessage = (message: Message) => {
  const { offerSchedule, presentSchedule, scheduleMeetingFlow, appointmentInfo } = message.attributes
  return !isNil(offerSchedule) || !isNil(presentSchedule) || !isNil(scheduleMeetingFlow) || !isNil(appointmentInfo)
}

export const getMessageAttribute = <K extends MessageAttributeKeys>(message: Partial<Message>, attribute: K) =>
  pathOr<MessageAttributes[K]>(null, ['attributes', attribute], message)

export const hasMessageAttribute = <K extends MessageAttributeKeys>(message: Partial<Message>, attribute: K): boolean =>
  !!getMessageAttribute(message, attribute)

export const isEndUserMessage = (message: Partial<Message>) => {
  if (!message) { return false }

  return message.authorType === MessageAuthorType.LEAD || message.authorType === MessageAuthorType.END_USER
}

export const hasAgentMessage = any<Message>(message => message.authorType === MessageAuthorType.USER && !hasMessageAttribute(message, 'isBot'))

export const isRateConversationCTAMessage = (message: Message): boolean => (
  message.contentType === 'BOT_CONVERSATION_RATING' &&
  message.authorType === MessageAuthorType.USER &&
  hasMessageAttribute(message, 'ratingPrompt')
)

export const isConversationRatingMessage = (message: Message): boolean => (
  message.contentType === 'BOT_CONVERSATION_RATING' &&
  hasMessageAttribute(message, 'conversationRating')
)

export const truncateMessageText = (text: string): string => splitAt(100, text)[0].trim()

export const truncateMessagePreview = (message: Partial<Message>, limit: number): Partial<Message> => {
  const previewText = getMessageAttribute(message, 'preview')

  if (previewText && previewText.length > limit) {
    const truncatedMessage = truncateMessageText(previewText)
    return {
      ...message,
      attributes: {
        ...message.attributes,
        preview: `${truncatedMessage}...`
      }
    }
  }

  return message
}

export const getMessagePreviewText = (message: Partial<Message>): string => {
  if (message.contentType === 'BOT_CONVERSATION_RATING') {
    return truncateMessageText(getMessageAttribute(message, 'ratingPrompt').ratingPromptMessage)
  }

  return getMessageAttribute(message, 'preview')
}

export const getMessageCreatedAt = (message: Message): number =>
  message.createdAt || getMessageAttribute(message, 'generatedAt')

export const isButtonsOnlyMessage = (message: Message): boolean =>
  !!message &&
  !isEndUserMessage(message) &&
  !!getMessageAttribute(message, 'isButtonOnly') &&
  !isNilOrEmpty(getMessageAttribute(message, 'buttons'))

export const messageContainsButtons = (message: Message): boolean =>
!!message &&
!isEndUserMessage(message) &&
!isNilOrEmpty(getMessageAttribute(message, 'buttons'))

export const containsPermacloseMessage = (messages: Message[]): boolean =>
  any((message) => hasMessageAttribute(message, 'permacloseMessage'), messages)

